import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { MessagesSquare, Book, Brain, Zap, Bot, Menu, Eye, Instagram, Facebook } from 'lucide-react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Nav } from '@/components/nav'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { AccountSwitcher } from '@/components/account-switcher'
import { accounts } from '@/lib/accounts'
import { useIsMobile } from '@/hooks/use-mobile'
import { FEATURE_PREVIEW_IA } from '@/config/feature-flags'

interface SidebarProps {
  children: React.ReactNode
  className?: string
  /**
   * El panel de la derecha, cuando hay alguno abierto (2.3, D1).
   *
   * **Entra como una zona más del armazón, no encima de él.** Es lo que
   * distingue este panel de un cajón: el contenido se estrecha y se sigue
   * viendo, en vez de quedar tapado. Si tapara, volveríamos a obligar a
   * abandonar lo que estabas haciendo, que es el problema que el panel existe
   * para quitar.
   */
  panel?: React.ReactNode
}

export function Sidebar({ children, className = "", panel = null }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const location = useLocation()
  const previewHref = useMemo(() => "/preview-ia", [])
  const isConversationsRoute = location.pathname.includes('/conversations')
  // La insignia del buzon. Cero se pinta como cadena vacia: una insignia con un
  // «0» dice que hay algo que mirar y no lo hay.
  const shouldHideMobileTopbar = isConversationsRoute

  // **El Brain vuelve al menu** (brain-conversacion-en-segundo-plano, D6).
  // Salio el 2026-09-22 (brain-en-cualquier-pagina, 2.2) por ser una segunda
  // puerta al boton de la esquina, y Daniel lo pidio de vuelta el 2026-09-25:
  // la pantalla completa, con su historial, es un sitio al que se va, y no
  // tener donde encontrarlo en el menu lo escondia. **Son dos puertas a
  // proposito**: el boton abre el panel al lado de lo que estas haciendo, y
  // esta entrada lleva a la pantalla entera.
  const navigationLinks = [
    {
      title: t('functions.rkBrain'),
      label: "",
      icon: Brain,
      variant: "ghost" as const,
      href: "/brain",
    },
    // Preview IA usaba el icono `Brain` y eso hacia creer que el Brain ya
    // existia. Es otra cosa: probar tu agente como lo haria un cliente.
    ...(FEATURE_PREVIEW_IA ? [{
      title: "Preview IA",
      label: "",
      icon: Eye,
      variant: "ghost" as const,
      href: previewHref,
    }] : []),
    {
      title: t('functions.conversations'),
      label: "",
      icon: MessagesSquare,
      variant: "default" as const,
      href: "/conversations",
    },
    {
      title: t('functions.instagram'),
      label: "",
      icon: Instagram,
      variant: "ghost" as const,
      href: "/instagram",
    },
    {
      title: t('functions.facebook'),
      label: "",
      icon: Facebook,
      variant: "ghost" as const,
      href: "/facebook",
    },
    {
      title: t('functions.knowledge'),
      label: "",
      icon: Book,
      variant: "ghost" as const,
      href: "/knowledge",
    },
    {
      title: t('functions.actions'),
      label: t('functions.alpha_badge'),
      icon: Zap,
      variant: "ghost" as const,
      href: "/actions",
    },
    {
      title: t('agent_studio.nav_title'),
      label: "",
      icon: Bot,
      variant: "ghost" as const,
      href: "/agents",
      children: [
        {
          title: t('functions.agents') || "Agents",
          href: "/agents",
        },
        {
          title: t('functions.skills'),
          href: "/skills",
        },
        {
          title: t('functions.tools') || "Tools",
          href: "/tools",
        },
        {
          title: t('functions.channel_agents') || "Channel Agents",
          href: "/channel-agents",
        },
        {
          title: t('agent_studio.call_agents_link') || "Call agents",
          href: "/agent-studio/call-agents",
        },
        {
          title: t('functions.keys_secrets') || "Keys & Secrets",
          href: "/keys-secrets",
        },
        {
          title: t('functions.billing'),
          href: "/billing",
        },
      ],
    },
  ]

  // Allow children to open the drawer via a lightweight DOM event
  useEffect(() => {
    if (!isMobile) return
    const handler = () => setDrawerOpen(true)
    window.addEventListener('rk:open-mobile-menu', handler)
    return () => window.removeEventListener('rk:open-mobile-menu', handler)
  }, [isMobile])


  // ── MOBILE ──
  if (isMobile) {
    return (
      <div className={`flex h-dvh flex-col overflow-hidden ${className}`}>
        {/* Fixed header with hamburger */}
        {!shouldHideMobileTopbar && (
          <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-background px-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDrawerOpen(true)}
              aria-label={t('functions.main_navigation')}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <span className="truncate text-sm font-semibold">RK Platform</span>
          </header>
        )}

        {/* El cajon del menu. Eran 62 lineas a mano -velo, trampa de foco,
            Escape, scroll bloqueado y foco devuelto-; ahora es `Sheet`, que es
            donde eso se construye una vez. */}
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent side="left" sinCerrar className="w-72 pb-[max(env(safe-area-inset-bottom),0px)]">
            <SheetTitle className="sr-only">{t('functions.main_navigation')}</SheetTitle>
            <div className="flex h-[52px] items-center justify-center px-2">
              <AccountSwitcher isCollapsed={false} accounts={accounts} />
            </div>
            <Separator />
            <Nav
              isCollapsed={false}
              links={navigationLinks}
              showSettingsShortcut={true}
              onNavigate={() => setDrawerOpen(false)}
            />
          </SheetContent>
        </Sheet>

        {/* Full-width content area */}
        <main className="min-h-0 flex-1 overflow-y-auto pb-[max(env(safe-area-inset-bottom),0px)]">
          {children}
        </main>
      </div>
    )
  }

  // ── DESKTOP (sin cambios) ──
  return (
    <div className={`flex h-dvh overflow-hidden ${className}`}>
      <TooltipProvider delayDuration={0}>
        {/* **Ni una `key` que cambie aqui.** Era la forma facil de que el
            grupo rehiciera el reparto al aparecer la tercera zona, y remonta
            todo lo de dentro: la pagina de detras perderia su estado cada vez
            que se abre el panel, que es justo lo que 2.4 prohibe. La forma
            buena es que cada zona se identifique -`id` y `order`-, que es
            para lo que la libreria los trae. */}
        <ResizablePanelGroup
          direction="horizontal"
          className="h-full min-h-0 items-stretch"
        >
          {/* Left Panel - Navigation */}
          <ResizablePanel
            id="navegacion"
            order={1}
            defaultSize={20}
            collapsedSize={4}
            collapsible={true}
            minSize={15}
            maxSize={20}
            onCollapse={() => setIsCollapsed(true)}
            onExpand={() => setIsCollapsed(false)}
            className="bg-background"
          >
            <div className="flex h-[52px] items-center justify-center px-2">
              <AccountSwitcher isCollapsed={isCollapsed} accounts={accounts} />
            </div>
            <Separator />
            <Nav
              isCollapsed={isCollapsed}
              links={navigationLinks}
              showSettingsShortcut={true}
            />
          </ResizablePanel>
          <ResizableHandle withHandle />

          {/* Main Content Area. **Su minimo baja cuando hay panel**: con 60
              no cabrian las tres zonas, y el grupo recortaria el panel hasta
              dejarlo inutil en vez de avisar. */}
          <ResizablePanel
            id="contenido"
            order={2}
            defaultSize={panel ? 52 : 80}
            minSize={panel ? 40 : 60}
            className="h-full min-h-0"
          >
            <div className="h-full min-h-0 overflow-y-auto">
              {children}
            </div>
          </ResizablePanel>

          {/* La tercera zona. Se puede ensanchar arrastrando, que sale gratis
              por estar aqui dentro: el armazon ya era un grupo de paneles. */}
          {panel && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel
                id="panel"
                order={3}
                defaultSize={28}
                minSize={20}
                maxSize={45}
                className="h-full min-h-0"
              >
                {panel}
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
      </TooltipProvider>
    </div>
  )
}
